// ============================================================================
// Feuille de brouillon : DÉMONSTRATION DE GÉOMÉTRIE jugée par le noyau
// (moteur v25, refonte lot 5.4 — docs/moteur/REFONTE_MOTEUR.md).
// ============================================================================
// L'énoncé (`kernel.start` puis `givens`) est une suite de propriétés et de
// longueurs ; l'élève écrit UNE propriété ou UNE relation entre longueurs par ligne.
//   - fausse sur la figure de référence           → « C'est faux »
//   - déjà dans l'énoncé ou sur la feuille        → « Tu le sais déjà » (gardable)
//   - donnée par une règle du paquet geometry-core → pas de cours (2 HORA)
//   - calculée à partir des longueurs connues      → « Calculer » (1 HORA)
//   - vraie, mais d'aucune de ces façons           → « D'où vient-elle ? »
// Le but : la propriété demandée (« (MN) ∦ (BC) ») ou la longueur (« IJ = 4 »).
// Le corrigé (`kernel.path`) est obligatoire : il sert au coup de pouce.
// ============================================================================

import type { Chip } from './lexer';
import { lex } from './lexer';
import type { KernelSpec, ScratchModel } from './model';
import { atomsText, type Line } from './tree';
import { evalFloat } from './kernel/normal';
import { canon, substitute, symbolsOf, termKey, termText, type Term } from './kernel/term';
import {
  Figure,
  GeoBase,
  GEO_RELATIONS,
  GeometrySyntaxError,
  constTruth,
  geometryRules,
  gpropChips,
  gpropPoints,
  gpropText,
  looksGeometric,
  normalizeGeoText,
  parseGProp,
  parseMetric,
  relDebt,
  relHoldsIn,
  relSegments,
  sameRelation,
  substRel,
  valueOf,
  type GeoFact,
  type GeoRule,
  type GProp,
  type MLine,
  type MRel,
} from './kernel/geometry';

/** Identifiant du calcul (ligne de longueurs justifiée sans règle de cours). */
export const GEOMETRY_COMPUTE_ID = 'kernel.compute@1';

export interface KGeoVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  /** la ligne lue (enregistrée par `kgeoCommit`) */
  gfact?: GeoFact;
}

export interface KGeoState {
  spec: KernelSpec;
  figure: Figure;
  lengths: Record<string, number>;
  points: Set<string>;
  givens: GeoFact[];
  lines: GeoFact[];
  answer: GeoFact;
  traps: { fact: GeoFact; title: string; why: string; line: Line }[];
  path: string[];
  rules: readonly GeoRule[];
  /** atomes des lignes du corrigé : une ligne qui en est le début n'est pas encore jugée */
  known: string[][] | null;
}

/* ------------------------------ lecture ------------------------------ */

/** Une ligne de géométrie, gardée telle quelle (le moteur des égalités ne la lit pas). */
export function kgeoLine(text: string): Line {
  return { dis: [{ sides: [{ k: 's', n: text }] }] };
}

const isGeoToken = (a: string): boolean =>
  (GEO_RELATIONS as readonly string[]).includes(a) ||
  a === '=' ||
  a === '≠' ||
  (a.length > 1 && /^[([]/.test(a) && /[)\]]$/.test(a));

/** Texte d'une ligne à partir de ses bouts (« (MN) », « // »… ; « AM », « ÷ »…). */
export function kgeoTextOf(atoms: string[]): string {
  const out: string[] = [];
  let buf: string[] = [];
  const flush = () => {
    if (buf.length) out.push(atomsText(buf));
    buf = [];
  };
  for (const a of atoms) {
    if (isGeoToken(a)) {
      flush();
      out.push(a);
    } else buf.push(a);
  }
  flush();
  return normalizeGeoText(out.join(' '));
}

/** Les bouts d'une ligne écrite (énoncé, corrigé, coup de pouce). */
export function kgeoLineAtoms(text: string): string[] {
  if (looksGeometric(text)) return gpropChips(text);
  const parts = normalizeGeoText(text).split(/\s*(=|≠)\s*/);
  return parts.flatMap((p, i) => (i % 2 ? [p] : lex(p)));
}

/** Lit une ligne : une proposition, ou une relation entre longueurs. */
export function kgeoReadFact(text: string, points: ReadonlySet<string>, figure?: Figure): GeoFact {
  const t = normalizeGeoText(text);
  if (looksGeometric(t)) {
    const g = parseGProp(t);
    const missing = gpropPoints(g).find((p) => !points.has(p));
    if (missing) throw new GeometrySyntaxError(`Il n’y a pas de point ${missing} sur la figure.`);
    if (figure)
      for (const l of [g.k === 'par' || g.k === 'npar' || g.k === 'perp' ? [g.a, g.b] : []]
        .flat()
        .concat(g.k === 'online' ? [g.l] : []))
        if (l.k === 'name' && !figure.spec.lines?.[l.n])
          throw new GeometrySyntaxError(`La droite (${l.n}) n’est pas sur la figure.`);
    return { text: gpropText(g), g };
  }
  const m = parseMetric(t, points);
  if (!m.ok) throw new GeometrySyntaxError(m.why);
  return { text: t, m: m.m };
}

/** relations d'une ligne de longueurs vues comme faits (chaîne « = » : toutes les paires) */
function factRels(m: MLine): MRel[] {
  if (m.ops.some((o) => o !== '=') || m.members.length < 3) return m.rels;
  const out: MRel[] = [];
  for (let i = 0; i < m.members.length; i++)
    for (let j = i + 1; j < m.members.length; j++)
      out.push({ op: '=', l: m.members[i]!, r: m.members[j]! });
  return out;
}
function asFact(f: GeoFact): GeoFact {
  return f.m ? { ...f, m: { ...f.m, rels: factRels(f.m) } } : f;
}

/* ------------------------------ état ------------------------------ */

export function createKGeoState(model: ScratchModel): KGeoState {
  const spec = model.kernel!;
  const figure = new Figure(spec.figure!);
  const points = new Set(Object.keys(spec.figure!.points));
  const read = (t: string) => kgeoReadFact(t, points, figure);
  return {
    spec,
    figure,
    lengths: figure.lengths(),
    points,
    givens: [spec.start, ...(model.givens ?? [])].map(read),
    lines: [],
    answer: read(spec.answer),
    traps: (model.traps ?? []).flatMap((t) => {
      try {
        return [{ fact: read(t.line), title: t.title, why: t.why, line: kgeoLine(t.line) }];
      } catch {
        return [];
      }
    }),
    path: spec.path ?? [],
    rules: geometryRules(),
    known: null,
  };
}

function baseOf(ks: KGeoState, extra: GeoFact[] = []): GeoBase {
  return new GeoBase([...ks.givens, ...ks.lines, ...extra].map(asFact), ks.points, ks.rules);
}

function holdsInFigure(ks: KGeoState, f: GeoFact): boolean {
  if (f.g) return ks.figure.holds(f.g);
  return f.m!.rels.every((r) => relHoldsIn(r, ks.lengths));
}

/** même proposition (à alignement près) / mêmes relations */
function sameFact(base: GeoBase, a: GeoFact, b: GeoFact): boolean {
  if (a.g && b.g) return base.sameProp(a.g, b.g);
  if (a.m && b.m) return a.m.rels.every((r) => b.m!.rels.some((s) => sameRelation(r, s)));
  return false;
}

/* ------------------------------ juge ------------------------------ */

function knownAtoms(ks: KGeoState): string[][] {
  ks.known ??= [...ks.path, ks.spec.answer].map(kgeoLineAtoms);
  return ks.known;
}
function strictPrefixOfKnown(ks: KGeoState, A: string[]): boolean {
  return knownAtoms(ks).some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
}
const OPEN_END = new Set(['+', '−', '×', '÷', '(', '^', '√', '=', '≠', '²']);
function looksOpen(A: string[], text: string): boolean {
  const last = A[A.length - 1]!;
  if ((GEO_RELATIONS as readonly string[]).includes(last) && last !== 'parallélogramme')
    return !['rectangle', 'losange', 'carré', 'n’est pas rectangle'].includes(last);
  if (OPEN_END.has(last) && last !== '²') return true;
  // une seule chose écrite (un point, une droite, une longueur) : la ligne commence
  if (!looksGeometric(text) && !/[=≠]/.test(text)) return true;
  return A.length === 1;
}

export function kgeoJudge(ks: KGeoState, chips: Chip[], opts?: { verify?: boolean }): KGeoVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const text = kgeoTextOf(A);
  const prefix = strictPrefixOfKnown(ks, A);
  let fact: GeoFact;
  try {
    fact = kgeoReadFact(text, ks.points, ks.figure);
  } catch (e) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    if (looksOpen(A, text)) return { kind: 'open', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: e instanceof GeometrySyntaxError ? e.message : 'Ligne illisible.',
      badChip: chips.length - 1,
    };
  }
  const v = evaluate(ks, fact, A);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready' };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);

function evaluate(ks: KGeoState, fact: GeoFact, A: string[]): KGeoVerdict {
  const line = kgeoLine(fact.text);
  const base = baseOf(ks);
  const trap = ks.traps.find((t) => sameFact(base, fact, t.fact));
  if (trap)
    return {
      kind: 'trap',
      atoms: A,
      line,
      title: 'Piège : ' + lower(trap.title),
      message: trap.why,
      trap: { line: trap.line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  return fact.g ? evaluateProp(ks, base, fact, A, line) : evaluateMetric(ks, base, fact, A, line);
}

const KNOWN: Pick<KGeoVerdict, 'title' | 'message'> = {
  title: 'Tu le sais déjà',
  message: 'C’est déjà dans l’énoncé ou sur ta feuille.',
};

function falsePropMessage(g: GProp): string {
  switch (g.k) {
    case 'par':
      return 'Ces deux droites ne sont pas parallèles.';
    case 'npar':
      return 'Ces deux droites sont parallèles.';
    case 'perp':
      return 'Ces deux droites ne sont pas perpendiculaires.';
    case 'mid':
      return `${g.p} n’est pas le milieu de [${g.a}${g.b}].`;
    case 'onseg':
      return `${g.p} n’est pas sur le segment [${g.a}${g.b}].`;
    case 'online':
      return `${g.p} n’est pas sur cette droite.`;
    case 'para':
      return `${g.v.join('')} n’est pas un parallélogramme (attention à l’ordre des sommets).`;
    case 'rectangle':
      return `${g.v.join('')} n’est pas un rectangle.`;
    case 'losange':
      return `${g.v.join('')} n’est pas un losange.`;
    case 'carre':
      return `${g.v.join('')} n’est pas un carré.`;
    case 'rt':
      return `Le triangle ${g.at}${g.b}${g.c} n’est pas rectangle en ${g.at}.`;
    case 'iso':
      return `Le triangle ${g.at}${g.b}${g.c} n’est pas isocèle en ${g.at}.`;
    case 'nrt':
      return `Le triangle ${g.v.join('')} est rectangle.`;
    case 'bis':
      return `${g.p} n’est pas sur la médiatrice de [${g.a}${g.b}].`;
    case 'circ':
      return `${g.p} n’est pas sur le cercle de diamètre [${g.a}${g.b}].`;
  }
}

function stepVerdict(
  ks: KGeoState,
  fact: GeoFact,
  A: string[],
  line: Line,
  rule: GeoRule | null,
  calcLabel: string,
): KGeoVerdict {
  const final = isAnswerFact(ks, fact);
  const hora = rule ? 2 : 1;
  const why = final
    ? 'C’est ce qu’il fallait démontrer.'
    : rule
      ? rule.label + '.'
      : calcLabel === 'Comparer'
        ? 'Tu compares deux nombres calculés avec les longueurs connues.'
        : 'Tu remplaces les longueurs connues et tu calcules.';
  return {
    kind: 'step',
    atoms: A,
    line,
    final,
    hora,
    rule: {
      id: rule ? rule.qid : GEOMETRY_COMPUTE_ID,
      card: rule ? rule.card : '',
      label: rule ? rule.label : calcLabel,
      hora,
      why,
    },
    gfact: fact,
  };
}

function evaluateProp(
  ks: KGeoState,
  base: GeoBase,
  fact: GeoFact,
  A: string[],
  line: Line,
): KGeoVerdict {
  const g = fact.g!;
  if (!ks.figure.holds(g))
    return { kind: 'false', atoms: A, line, title: 'C’est faux', message: falsePropMessage(g) };
  if (base.established(g)) return { kind: 'useless', atoms: A, line, ...KNOWN, gfact: fact };
  const rule = base.justifyProp(g);
  if (rule) return stepVerdict(ks, fact, A, line, rule, '');
  return {
    kind: 'unjustified',
    atoms: A,
    line,
    title: 'D’où vient-elle ?',
    message:
      'C’est vrai, mais ça ne découle pas encore de ce que tu as écrit. Cherche une propriété du cours dont toutes les conditions sont dans l’énoncé ou sur ta feuille.',
  };
}

type RelClass = 'known' | 'rewrite' | 'rule' | 'calc' | 'num' | 'unjustified';

const hasSegments = (t: Term): boolean => symbolsOf(t).size > 0;
/**
 * Forme « résolue » : une longueur, son carré ou un rapport de deux longueurs égal à un
 * nombre (« BC = 7 », « AM ÷ AB = 2 ÷ 3 »). Clé de ce qui est calculé, sinon null.
 */
function solvedKey(r: MRel): string | null {
  if (r.op !== '=') return null;
  for (const [x, c] of [
    [r.l, r.r],
    [r.r, r.l],
  ] as [Term, Term][]) {
    if (hasSegments(c)) continue;
    const ok =
      x.k === 'sym' ||
      (x.k === 'pow' && x.a.k === 'sym' && x.b.k === 'num') ||
      (x.k === 'div' && x.a.k === 'sym' && x.b.k === 'sym');
    if (ok) return termKey(canon(x));
  }
  return null;
}

/** valeur d'une expression de longueurs : longueurs connues, ou une ligne « E = nombre » */
function exprValue(base: GeoBase, e: Term): Term | null {
  const v = valueOf(e, base.V);
  if (v) return v;
  for (const f of base.rels) {
    if (f.op !== '=') continue;
    for (const c of [f.l, f.r]) {
      const cv = valueOf(c, base.V);
      if (!cv || hasSegments(c)) continue;
      if (sameRelation(substRel({ op: '=', l: e, r: cv }, base.V), substRel(f, base.V))) return cv;
    }
  }
  return null;
}

/** la relation découle de deux lignes : une égalité « s = E » remplacée dans une autre */
function substitutionFollows(base: GeoBase, r: MRel): boolean {
  for (const f1 of base.rels) {
    if (f1.op !== '=') continue;
    for (const [s, e] of [
      [f1.l, f1.r],
      [f1.r, f1.l],
    ] as [Term, Term][]) {
      if (s.k !== 'sym' || termKey(s) === termKey(e)) continue;
      const map = { [s.n]: e };
      const sub = (x: MRel): MRel => ({
        op: x.op,
        l: substitute(x.l, map),
        r: substitute(x.r, map),
      });
      const r1 = sub(r);
      for (const f2 of base.rels) {
        if (f2 === f1) continue;
        if (sameRelation(f2, r1) || sameRelation(sub(f2), r)) return true;
      }
    }
  }
  return false;
}

function classify(base: GeoBase, r: MRel): { c: RelClass; rule?: GeoRule } {
  const known = base.rels.filter((f) => sameRelation(f, r));
  if (known.length) {
    if (Math.min(...known.map(relDebt)) > relDebt(r)) return { c: 'rewrite' };
    // « BC = 7 » écrit après « 3,5 = BC ÷ 2 » : la valeur est enfin calculée
    const sk = solvedKey(r);
    if (sk && !known.some((f) => solvedKey(f) === sk)) return { c: 'rewrite' };
    return { c: 'known' };
  }
  const rule = base.justifyRel(r);
  if (rule) return { c: 'rule', rule };
  const rv = substRel(r, base.V);
  const ct = constTruth(rv);
  if (ct === true) return { c: relSegments(r).size ? 'calc' : 'num' };
  if (ct === null) {
    if (base.rels.some((f) => sameRelation(substRel(f, base.V), rv))) return { c: 'calc' };
    const lv = exprValue(base, r.l);
    const rr = exprValue(base, r.r);
    if (lv && rr && constTruth({ op: r.op, l: lv, r: rr }) === true) return { c: 'calc' };
    if (substitutionFollows(base, r)) return { c: 'calc' };
  }
  return { c: 'unjustified' };
}

function falseMetricMessage(base: GeoBase, m: MLine, ks: KGeoState): string {
  const bad = m.rels.find((r) => !relHoldsIn(r, ks.lengths)) ?? m.rels[0]!;
  const l = valueOf(bad.l, base.V);
  const r = valueOf(bad.r, base.V);
  // « AB = 10 » quand AB = 9 : la valeur connue, pas le calcul
  const lc = relSegments({ op: '=', l: bad.l, r: bad.l }).size === 0;
  const rc = relSegments({ op: '=', l: bad.r, r: bad.r }).size === 0;
  if (bad.op === '=' && l && r && lc !== rc) {
    const [side, val, other] = lc ? [bad.r, r, bad.l] : [bad.l, l, bad.r];
    return `${termText(side)} = ${termText(val)}, pas ${termText(other)}.`;
  }
  if (l && r) {
    const both = `${termText(bad.l)} = ${termText(l)} et ${termText(bad.r)} = ${termText(r)}`;
    return bad.op === '='
      ? `${both} : ces deux nombres ne sont pas égaux.`
      : `${both} : ces deux nombres sont égaux.`;
  }
  return 'Cette égalité n’est pas vraie pour la figure de l’énoncé. Vérifie avec les longueurs données.';
}

function evaluateMetric(
  ks: KGeoState,
  base: GeoBase,
  fact: GeoFact,
  A: string[],
  line: Line,
): KGeoVerdict {
  const m = fact.m!;
  if (!m.rels.every((r) => relHoldsIn(r, ks.lengths)))
    return {
      kind: 'false',
      atoms: A,
      line,
      title: 'C’est faux',
      message: falseMetricMessage(base, m, ks),
    };
  const cls = m.rels.map((r) => classify(base, r));
  if (cls.some((x) => x.c === 'unjustified'))
    return {
      kind: 'unjustified',
      atoms: A,
      line,
      title: 'D’où vient-elle ?',
      message:
        'Cette égalité est juste, mais elle ne découle pas encore de ce que tu as écrit : quelle propriété du cours la donne ?',
    };
  if (cls.every((x) => x.c === 'known' || x.c === 'num')) {
    if (cls.some((x) => x.c === 'known'))
      return { kind: 'useless', atoms: A, line, ...KNOWN, gfact: fact };
    return {
      kind: 'useless',
      atoms: A,
      line,
      title: 'Juste, mais…',
      message:
        'Ce calcul est juste, mais il ne dit rien de la figure : écris la longueur ou le rapport que tu calcules.',
      gfact: fact,
    };
  }
  const rule = cls.find((x) => x.c === 'rule')?.rule ?? null;
  const compare = m.ops.includes('≠');
  if (!rule && compare && m.rels.length === 1 && wrongPythagoreSide(ks, m.rels[0]!))
    return {
      kind: 'useless',
      atoms: A,
      line,
      title: 'Juste, mais à côté',
      message:
        'Pour savoir si le triangle est rectangle, compare le carré du PLUS GRAND côté à la somme des carrés des deux autres.',
      gfact: fact,
    };
  return stepVerdict(ks, fact, A, line, rule, compare ? 'Comparer' : 'Calculer');
}

/** « AB² » → « AB » */
function squaredSegment(t: Term): string | null {
  return t.k === 'pow' && t.a.k === 'sym' && termText(t.b) === '2' ? t.a.n : null;
}
/**
 * « AB² ≠ AC² + BC² » quand AB n'est pas le plus grand côté : la comparaison est juste
 * mais ne dit rien (la contraposée de Pythagore compare le plus grand côté).
 */
function wrongPythagoreSide(ks: KGeoState, r: MRel): boolean {
  for (const [x, y] of [
    [r.l, r.r],
    [r.r, r.l],
  ] as [Term, Term][]) {
    const alone = squaredSegment(x);
    if (!alone || y.k !== 'add' || y.c.length !== 2) continue;
    const pair = y.c.map(squaredSegment);
    if (!pair[0] || !pair[1]) continue;
    const len = (s: string): number => ks.lengths[s] ?? Number.NaN;
    const longest = Math.max(len(pair[0]), len(pair[1]));
    if (len(alone) < longest * (1 - 1e-9)) return true;
  }
  return false;
}

/* ------------------------------ feuille ------------------------------ */

export function kgeoCommit(ks: KGeoState, v: KGeoVerdict): void {
  if (v.gfact && (v.kind === 'step' || v.kind === 'ready' || v.kind === 'useless'))
    ks.lines.push(v.gfact);
}

/** la ligne écrit-elle la réponse (propriété démontrée, ou longueur calculée jusqu'au bout) ? */
function isAnswerFact(ks: KGeoState, f: GeoFact): boolean {
  const a = ks.answer;
  if (a.g) return !!f.g && sameFact(baseOf(ks), f, a);
  if (!f.m) return false;
  const rels = factRels(f.m);
  return a.m!.rels.every((ar) => rels.some((r) => sameRelation(r, ar) && relDebt(r) === 0));
}

export function kgeoGoalReached(ks: KGeoState): boolean {
  return ks.lines.some((l) => isAnswerFact(ks, l));
}

/** La ligne (texte) est-elle établie (énoncé, feuille, ou équivalente) ? */
export function kgeoEstablished(ks: KGeoState, text: string): boolean {
  let f: GeoFact;
  try {
    f = kgeoReadFact(text, ks.points, ks.figure);
  } catch {
    return false;
  }
  const base = baseOf(ks);
  if (f.g) return base.established(f.g);
  return f.m!.rels.every((r) => {
    const c = classify(base, r).c;
    return c === 'known' || c === 'num';
  });
}

/** Le texte (choix du QCM) dit-il la réponse attendue ? (sans regarder la feuille) */
export function kgeoSameAsAnswer(ks: KGeoState, text: string): boolean {
  try {
    const f = kgeoReadFact(text, ks.points, ks.figure);
    const a = ks.answer;
    if (a.g) return !!f.g && sameFact(baseOf(ks), f, a);
    return !!f.m && a.m!.rels.every((ar) => factRels(f.m!).some((r) => sameRelation(r, ar)));
  } catch {
    return false;
  }
}

export function kgeoIsAnswer(ks: KGeoState, text: string): boolean {
  return kgeoGoalReached(ks) && kgeoSameAsAnswer(ks, text);
}

/** La ligne (texte) est-elle vraie sur la figure ? (porte de validation) */
export function kgeoHolds(ks: KGeoState, text: string): boolean {
  try {
    return holdsInFigure(ks, kgeoReadFact(text, ks.points, ks.figure));
  } catch {
    return false;
  }
}

/** valeurs établies de la longueur cherchée (choix du QCM en valeur) */
export function kgeoStatedValues(ks: KGeoState): number[] {
  if (!kgeoGoalReached(ks) || !ks.answer.m) return [];
  const v = valueOf(ks.answer.m.rels[0]!.r, new Map());
  return v ? [evalFloat(v, {})] : [];
}

/** Coup de pouce : la prochaine ligne du corrigé pas encore établie. */
export function kgeoNextMove(
  ks: KGeoState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (kgeoGoalReached(ks)) return null;
  const next = ks.path.find((t) => !kgeoEstablished(ks, t)) ?? ks.spec.answer;
  const atoms = kgeoLineAtoms(next);
  const v = kgeoJudge(ks, atoms.map((a) => ({ atoms: [a], text: a })) as Chip[], { verify: true });
  return {
    line: kgeoLine(normalizeGeoText(next)),
    text: normalizeGeoText(next),
    atoms,
    label: v.kind === 'step' || v.kind === 'ready' ? (v.rule?.label ?? '') : '',
  };
}

/** Le corrigé rejoué ligne à ligne (porte, palette). */
export function kgeoSolvePath(model: ScratchModel): {
  lines: { text: string; line: Line; v: KGeoVerdict }[];
  reached: boolean;
} {
  const ks = createKGeoState(model);
  const lines = ks.path.map((text) => {
    const atoms = kgeoLineAtoms(text);
    const v = kgeoJudge(ks, atoms.map((a) => ({ atoms: [a], text: a })) as Chip[], {
      verify: true,
    });
    if (v.kind === 'step') kgeoCommit(ks, v);
    return { text: normalizeGeoText(text), line: kgeoLine(normalizeGeoText(text)), v };
  });
  return { lines, reached: kgeoGoalReached(ks) };
}
